import type { ReactNode } from 'react';
import { formatGbp, formatPct } from '../api';
import type { DecisionAction, SessionStatus, StreamKind } from '../types';

/** A framed panel with a titled header bar. The base container for the desk. */
export function Panel({
  title,
  note,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel${className ? ` ${className}` : ''}`}>
      {(title || actions) && (
        <div className="panel-head">
          <h2>
            {title}
            {note && <span className="panel-title-note">{note}</span>}
          </h2>
          {actions && <div className="keys">{actions}</div>}
        </div>
      )}
      <div className="panel-body">{children}</div>
    </section>
  );
}

/** A large tabular figure with a corner accent tick. */
export function Figure({
  label,
  value,
  foot,
  tone,
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
  tone?: 'up' | 'down' | 'flat';
}) {
  return (
    <div className="figure">
      <div className="figure-k">{label}</div>
      <div className={`figure-v${tone ? ` ${tone}` : ''}`}>{value}</div>
      {foot && <div className="figure-foot">{foot}</div>}
    </div>
  );
}

/** Signed money, coloured strictly by direction. */
export function Money({ value, showSign = false }: { value: number | null; showSign?: boolean }) {
  if (value === null || !Number.isFinite(value)) return <span className="flat">—</span>;
  const tone = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  const prefix = showSign && value > 0 ? '+' : '';
  return (
    <span className={tone}>
      {prefix}
      {formatGbp(value)}
    </span>
  );
}

export function Percent({ value, decimals = 2 }: { value: number | null; decimals?: number }) {
  if (value === null || !Number.isFinite(value)) return <span className="flat">—</span>;
  const tone = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  return <span className={tone}>{formatPct(value, decimals)}</span>;
}

/**
 * A sharp rectangular tag. The optional `mark` is a typographic glyph rendered
 * before the label (e.g. an arrow or bullet character), never a coloured dot.
 */
export function Tag({
  children,
  tone = 'neutral',
  mark,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'amber' | 'up' | 'down' | 'warn';
  mark?: string;
}) {
  const cls = tone === 'neutral' ? 'tag' : `tag tag-${tone}`;
  return (
    <span className={cls} {...(mark ? { 'data-mark': mark } : {})}>
      {children}
    </span>
  );
}

export function KindTag({ kind }: { kind: StreamKind }) {
  if (kind === 'MOMENTUM') return <Tag mark="↗">MOMENTUM</Tag>;
  if (kind === 'OPTIONS') return <Tag mark="∂">OPTIONS</Tag>;
  if (kind === 'AI') return <Tag tone="amber" mark="◆">GROQ AI</Tag>;
  if (kind === 'PATTERN') return <Tag mark="▚">PATTERN</Tag>;
  return <Tag tone="amber" mark="⋈">AI + PATTERN</Tag>;
}

export function ActionTag({ action }: { action: DecisionAction }) {
  if (action === 'BUY') return <Tag tone="up" mark="▲">BUY</Tag>;
  if (action === 'SELL') return <Tag tone="down" mark="▼">SELL</Tag>;
  return <Tag mark="—">HOLD</Tag>;
}

/** Stream identity: a short vertical bar in the stream's colour plus its name. */
export function StreamName({ name, colour }: { name: string; colour: string }) {
  return (
    <span className="stream-tab">
      <span className="stream-bar" style={{ background: colour }} aria-hidden="true" />
      {name}
    </span>
  );
}

/** A session readout for the rail: exchange, state and the relevant time. */
export function SessionReadout({ label, session }: { label: string; session: SessionStatus }) {
  const detail = session.isOpen
    ? `→ ${new Date(session.closesAt ?? '').toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
    : session.reason.replace('-', ' ');
  return (
    <div className="readout">
      <span className="readout-key">{label}</span>
      <span className={`readout-val ${session.isOpen ? 'on' : 'off'}`}>
        {session.isOpen ? 'OPEN' : 'SHUT'} {detail}
      </span>
    </div>
  );
}

export function Note({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'error';
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={`note note-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <div>
        {title && <strong>{title}</strong>}
        <div className="reason">{children}</div>
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="spin" aria-hidden="true" />
      {label && <span>{label}</span>}
    </span>
  );
}

/** Horizontal usage meter for the daily trade allowance. */
export function Meter({ used, limit, colour }: { used: number; limit: number; colour?: string }) {
  const pct = limit === 0 ? 0 : Math.min(100, (used / limit) * 100);
  const tone = pct >= 100 ? 'var(--down)' : pct >= 70 ? 'var(--warn)' : (colour ?? 'var(--amber)');
  return (
    <div
      className="meter"
      role="progressbar"
      aria-valuenow={used}
      aria-valuemin={0}
      aria-valuemax={limit}
      aria-label={`${used} of ${limit} trades used today`}
    >
      <div className="meter-fill" style={{ width: `${pct}%`, background: tone }} />
    </div>
  );
}

/** Signed strength meter for pattern scores, centred on zero. */
export function ScoreMeter({ score }: { score: number }) {
  const clamped = Math.max(-1, Math.min(1, score));
  const width = Math.abs(clamped) * 50;
  const positive = clamped >= 0;
  return (
    <div className="score" title={`Pattern score ${score.toFixed(3)}`}>
      <div className="score-mid" />
      <div
        className="score-fill"
        style={{
          background: positive ? 'var(--up)' : 'var(--down)',
          left: positive ? '50%' : `${50 - width}%`,
          width: `${width}%`,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Backward-compatible aliases.
//
// The pages beyond the dashboard were written against the previous component
// names. Rather than rewrite them all at once, these adapters map the old API
// onto the new terminal components so every screen adopts the new aesthetic
// while keeping its existing markup. New work should use the primitives above.
// ---------------------------------------------------------------------------

type LegacyTone = 'green' | 'red' | 'amber' | 'blue' | 'grey' | 'purple';

function mapTone(tone?: LegacyTone): 'neutral' | 'amber' | 'up' | 'down' | 'warn' {
  switch (tone) {
    case 'green':
      return 'up';
    case 'red':
      return 'down';
    case 'amber':
    case 'purple':
      return 'warn';
    case 'blue':
      return 'amber';
    default:
      return 'neutral';
  }
}

export function Card({
  title,
  subtitle,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const props: Parameters<typeof Panel>[0] = { children };
  if (title !== undefined) props.title = title;
  if (subtitle !== undefined) props.note = subtitle;
  if (actions !== undefined) props.actions = actions;
  if (className !== undefined) props.className = className;
  return <Panel {...props} />;
}

export function Stat({
  label,
  value,
  foot,
  tone,
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
  tone?: 'pos' | 'neg' | 'flat';
}) {
  const mapped = tone === 'pos' ? 'up' : tone === 'neg' ? 'down' : tone === 'flat' ? 'flat' : undefined;
  const props: Parameters<typeof Figure>[0] = { label, value };
  if (foot !== undefined) props.foot = foot;
  if (mapped !== undefined) props.tone = mapped;
  return <Figure {...props} />;
}

export function Badge({ children, tone }: { children: ReactNode; tone?: LegacyTone }) {
  return <Tag tone={mapTone(tone)}>{children}</Tag>;
}

export function KindBadge({ kind }: { kind: StreamKind }) {
  return <KindTag kind={kind} />;
}

export function ActionBadge({ action }: { action: DecisionAction }) {
  return <ActionTag action={action} />;
}

export function StreamChip({ name, colour }: { name: string; colour: string }) {
  return <StreamName name={name} colour={colour} />;
}

export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'error';
  title?: string;
  children: ReactNode;
}) {
  const props: Parameters<typeof Note>[0] = { tone, children };
  if (title !== undefined) props.title = title;
  return <Note {...props} />;
}

export function UsageBar({ used, limit, colour }: { used: number; limit: number; colour?: string }) {
  const props: Parameters<typeof Meter>[0] = { used, limit };
  if (colour !== undefined) props.colour = colour;
  return <Meter {...props} />;
}

export function SessionBadge({ label, session }: { label: string; session: SessionStatus }) {
  return <SessionReadout label={label} session={session} />;
}
